"use client";
import React from "react";
import Image from "next/image";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import Link from "next/link";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const FeatureShowcase = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const featuresRaw = t("featureShowcase.features", { returnObjects: true });
  const features = Array.isArray(featuresRaw) ? featuresRaw : [];

  const logos = [
    { img: IMAGES.ICON_1, alt: "Grab Solutions" },
    { img: IMAGES.ICON_2, alt: "UNITED SOL" },
    { img: IMAGES.ICON_3, alt: "Coltfox" },
    { img: IMAGES.ICON_4, alt: "PAKANGELS" },
    { img: IMAGES.ICON_5, alt: "IDC" },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, direction: isArabic ? "rtl" : "ltr" }}>
      <Container maxWidth="md">
        {/* Header */}
        <Typography
          component="h2"
          sx={{
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            fontWeight: "bold",
            fontSize: {
              xs: "1.75rem",
              sm: "2rem",
              md: "2.75rem",
              lg: "2.3rem",
              xl: "2.75rem",
            },
            lineHeight: 1.3,
            color: "#0F172A",
            fontFamily: "var(--font-plusjakarta)",
            px: { xs: 2, md: 0 },
          }}
        >
          {t("featureShowcase.heading1")}
        </Typography>

        <Typography
          variant="h2"
          component="h2"
          sx={{
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mb: { xs: 3, md: 5 },
            fontWeight: "bold",
            fontSize: {
              xs: "1.75rem",
              sm: "2rem",
              md: "2.75rem",
              lg: "2.3rem",
              xl: "2.75rem",
            },
            lineHeight: 1.3,
            color: "#0F172A",
            fontFamily: "var(--font-plusjakarta)",
            px: { xs: 2, md: 0 },
          }}
        >
          {t("featureShowcase.heading2")}
        </Typography>

        {/* Features */}
        <Stack spacing={2.5} sx={{ mb: 4, px: { xs: 2, md: 0 } }}>
          {features.map((feature, index) => (
            <Card
              key={index}
              elevation={0}
              sx={{
                background: "#F3EBFF",
                borderRadius: 3,
                border: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(139, 92, 246, 0.12)",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: isArabic ? "row" : "row",
                  gap: { xs: 1.5, md: 2.5 },
                  p: { xs: 2, md: 3 },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src={IMAGES.CORRECT_IMG_NEW}
                    alt={feature.title}
                    width={45}
                    height={45}
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "auto",
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, textAlign: isArabic ? "right" : "left" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      fontSize: { xs: "0.95rem", sm: "1rem", md: "1.2rem" },
                      color: "#231A30",
                      lineHeight: 1.4,
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#02061799",
                      lineHeight: 1.6,
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      fontFamily: "var(--font-outfit)",
                      fontWeight: 400,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* CTA Button */}
        <Box
          sx={{ textAlign: "center", mb: 4, pt: "2%", px: { xs: 2, md: 0 } }}
        >
          <Button
            component={Link}
            href="https://getgeek.ai/free-trial-of-ai-recruiting-tool"
            // target="_blank"
            // rel="noopener noreferrer"
            variant="contained"
            size="large"
            sx={{
              background: "#9048FB",
              color: "white",
              px: { xs: 3, md: 4 },
              py: 1.5,
              borderRadius: 50,
              textTransform: "none",
              fontSize: { xs: "1rem", md: "1.1rem" },
              fontWeight: 500,
              boxShadow: "none",
              fontFamily: "var(--font-outfit)",
              border: "none",
              transition: "transform 0.2s ease-in-out",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                background: "#000",
                boxShadow: "none",
                transform: "scale(1.05) translateY(-2px)",
              },
            }}
          >
            {t("featureShowcase.cta")}
          </Button>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "#02061799",
            mb: { xs: 4, md: 6 },
            maxWidth: 380,
            mx: "auto",
            lineHeight: 1.7,
            fontSize: { xs: "1rem", md: "1.2rem" },
            fontFamily: "var(--font-outfit)",
            fontWeight: "500",
            px: { xs: 2, md: 0 },
          }}
        >
          {t("featureShowcase.desc")}
        </Typography>

        {/* Recommended Box */}
        <Box sx={{ textAlign: "center", mb: 4, px: { xs: 2, md: 0 } }}>
          <Box
            sx={{
              display: "inline-block",
              background: "#F3E8FF",
              px: { xs: 2.5, md: 4 },
              py: 1.5,
              borderRadius: 50,
              border: "none",
              mt: "2%",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                textTransform: "none",
                letterSpacing: "0.3px",
                color: "#8200DB",
                fontFamily: "var(--font-outfit)",
                display: "block",
              }}
            >
              {t("featureShowcase.recommended")}
            </Typography>
          </Box>
        </Box>

        {/* Logos */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            gap: { xs: 2, md: 3 },
            maxWidth: "100%",
            mx: "auto",
            pt: "1%",
            px: { xs: 2, md: 0 },
          }}
        >
          {logos.map((logo, index) => (
            <Box
              key={index}
              sx={{
                background: "white",
                px: { xs: 1, md: 1.5 },
                py: { xs: 1, md: 1.2 },
                borderRadius: 3,
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                width: {
                  xs: "calc(50% - 8px)",
                  sm: "calc(50% - 12px)",
                  md: "auto",
                },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Image
                src={logo.img}
                alt={logo.alt}
                width={120}
                height={50}
                style={{
                  objectFit: "contain",
                  width: "auto",
                  height: "40px",
                  maxWidth: "100px",
                }}
              />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FeatureShowcase;
